import type { CaptureMode, ChannelType, PaymentType, PaymentTypeUpdate } from './paymentTypesApi'

/**
 * The Payment types form, as plain functions.
 *
 * Kept out of PaymentTypesPage.tsx for the same reason navVisibility.ts is
 * out of AppLayout: react-refresh/only-export-components warns on helpers
 * exported next to a component, and these are worth a unit test of their
 * own — a form that lets an invalid body through just round-trips a 400.
 */

/** Every field as the inputs hold it — position stays a string until submit. */
export interface PaymentTypeFormState {
  name: string
  description: string
  captureMode: CaptureMode
  channels: string[]
  applicableFor: string[]
  position: string
  active: boolean
}

/** Mirror the server's limits, so the form refuses before the 400 would. */
export const MAX_NAME_LENGTH = 100
export const MAX_DESCRIPTION_LENGTH = 500

/** The only transaction type today; pre-ticked so a new type is usable at once. */
export const DEFAULT_APPLICABLE_FOR = 'REPAYMENT'

/** Positions step by ten so a type can be slotted between two others later. */
export const POSITION_STEP = 10

export function formFromPaymentType(t: PaymentType | null, defaultPosition: number): PaymentTypeFormState {
  if (!t) {
    return {
      name: '',
      description: '',
      captureMode: 'MANUAL',
      channels: [],
      applicableFor: [DEFAULT_APPLICABLE_FOR],
      position: String(defaultPosition),
      active: true,
    }
  }
  // Copies, not the cached row's own arrays: ticking a box must never mutate
  // what react-query holds.
  return {
    name: t.name,
    description: t.description ?? '',
    captureMode: t.captureMode,
    channels: [...t.channels],
    applicableFor: [...t.applicableFor],
    position: String(t.position),
    active: t.active,
  }
}

/** Where a new type goes: after everything that exists, with room to reorder. */
export function nextPosition(existing: readonly { position: number }[]): number {
  if (existing.length === 0) return POSITION_STEP
  return Math.max(...existing.map((t) => t.position)) + POSITION_STEP
}

const NON_NEGATIVE_INTEGER = /^\d+$/

export function validatePaymentTypeForm(f: PaymentTypeFormState): string[] {
  const errors: string[] = []
  const name = f.name.trim()
  if (!name) errors.push('Payment type is required.')
  else if (name.length > MAX_NAME_LENGTH) errors.push(`Payment type must be at most ${MAX_NAME_LENGTH} characters.`)
  if (f.description.trim().length > MAX_DESCRIPTION_LENGTH) {
    errors.push(`Description must be at most ${MAX_DESCRIPTION_LENGTH} characters.`)
  }
  if (f.channels.length === 0) errors.push('Pick at least one channel type.')
  if (f.applicableFor.length === 0) errors.push('Pick at least one transaction type under Applicable for.')
  if (!NON_NEGATIVE_INTEGER.test(f.position.trim())) errors.push('Position must be a whole number, 0 or more.')
  return errors
}

export function toPaymentTypeRequest(f: PaymentTypeFormState): PaymentTypeUpdate {
  const description = f.description.trim()
  return {
    name: f.name.trim(),
    description: description || null,
    captureMode: f.captureMode,
    channels: [...f.channels],
    applicableFor: [...f.applicableFor],
    position: Number(f.position.trim()),
    active: f.active,
  }
}

/** The row as it is with only `active` flipped — a PUT replaces the whole row. */
export function activeToggleRequest(t: PaymentType): PaymentTypeUpdate {
  return {
    name: t.name,
    description: t.description,
    captureMode: t.captureMode,
    channels: [...t.channels],
    applicableFor: [...t.applicableFor],
    position: t.position,
    active: !t.active,
  }
}

/** "needs cheque number · carried by agent · digital" — what picking this channel implies. */
export function channelHint(c: ChannelType): string {
  const parts: string[] = []
  if (c.requiresReference) parts.push(`needs ${c.referenceLabel ? lowerFirst(c.referenceLabel) : 'reference'}`)
  if (c.carried) parts.push('carried by agent')
  if (c.digital) parts.push('digital')
  return parts.join(' · ')
}

/** "Cheque number" → "cheque number"; "UTR / transaction reference" keeps its acronym. */
function lowerFirst(label: string): string {
  return /^[A-Z]{2}/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1)
}
